import { ReactElement } from 'react';

import { AppIcons } from '@backrs_ui/common-ui/src/designSystem/icons/AppIcon';
import { formatDateToLocaleString } from '@backrs_ui/common-ui/src/utils/dateTime.util';

import { NuggetDefaultCoverImage } from './NuggetCareerPath/NuggetDefaultCoverImage';
import { NuggetEvent } from './NuggetEvent';
import { NuggetGeneric } from './NuggetGeneric';
import { NuggetProfile } from './NuggetProfile/NuggetProfile';
import { NuggetUXVariant } from './NuggetUITypes';
import { NuggetVideo } from './NuggetVideo';

const variantMapper = (
  variant: NuggetUXVariant,
  title: string,
  contentSize: 'SMALL' | 'LARGE',
  body: string | undefined,
  hideBody: boolean,
  fitContent: boolean,
  onClick?: () => void,
  subText?: string,
): ReactElement => {
  switch (variant.type) {
    case 'opportunity':
      return (
        <NuggetGeneric
          title={title}
          description={body}
          onClick={onClick}
          typeChipProps={[{ label: 'Opportunity', icon: 'Building01' }]}
          subText={subText}
          hideBody={hideBody}
          fitContent={fitContent}
        />
      );

    case 'event':
      return (
        <NuggetGeneric
          title={title}
          description={body}
          onClick={onClick}
          customHeader={
            <NuggetEvent
              variant={{
                type: 'SMALL',
                date: variant.date,
                previewImage: variant.previewImage,
              }}
            />
          }
          typeChipProps={[
            { label: 'Event', icon: 'CalendarPlus01' },
            { label: 'Event Series', icon: 'Calendar' },
          ]}
          subText={subText}
          hideBody={hideBody}
          fitContent={fitContent}
        />
      );

    case 'video-external':
      return (
        <NuggetGeneric
          title={title}
          description={body}
          customHeader={
            <NuggetVideo
              variant={{
                type: 'SMALL',
                url: variant.url,
                previewImage: variant.previewImage,
                isInternal: false,
              }}
            />
          }
          onClick={onClick}
          typeChipProps={[{ label: 'Video', icon: 'VideoRecorder' }]}
          subText={subText}
          hideBody={hideBody}
          fitContent={fitContent}
        />
      );

    case 'video-internal':
      return (
        <NuggetGeneric
          title={title}
          description={body}
          customHeader={
            <NuggetVideo
              variant={{
                type: 'SMALL',
                url: variant.url,
                previewImage: variant.previewImage,
                isInternal: true,
              }}
            />
          }
          onClick={onClick}
          typeChipProps={[
            { label: 'Video', icon: 'VideoRecorder' },
            { label: 'Real People', icon: 'User02' },
          ]}
          subText={subText}
          hideBody={hideBody}
          fitContent={fitContent}
        />
      );

    case 'article':
      return (
        <NuggetGeneric
          title={title}
          description={body}
          coverImage={variant.previewImage}
          onClick={onClick}
          typeChipProps={[{ label: 'Article', icon: 'File06' }]}
          subText={subText}
          hideBody={hideBody}
          fitContent={fitContent}
        />
      );

    case 'institution':
      return (
        <NuggetGeneric
          title={title}
          description={body}
          coverImage={variant.previewImage}
          onClick={onClick}
          typeChipProps={[{ label: 'University', icon: 'Bank' }]}
          subText={subText}
          hideBody={hideBody}
          fitContent={fitContent}
        />
      );

    case 'employer':
      return (
        <NuggetGeneric
          title={title}
          description={body}
          coverImage={variant.employerInfo?.banner_media_url}
          onClick={onClick}
          typeChipProps={[{ label: 'Employer', icon: 'File02' }]}
          subText={subText}
          hideBody={hideBody}
          fitContent={fitContent}
        />
      );

    case 'audio':
      return (
        <NuggetGeneric
          title={title}
          description={body}
          coverImage={variant.previewImage}
          onClick={onClick}
          typeChipProps={[
            { label: 'Audio', icon: 'VolumeMin' },
            { label: 'Resource', icon: 'BookOpen01' },
          ]}
          subText={subText}
          hideBody={hideBody}
          fitContent={fitContent}
        />
      );

    case 'document':
      return (
        <NuggetGeneric
          title={title}
          description={body}
          onClick={onClick}
          typeChipProps={[{ label: 'Document', icon: 'File02' }]}
          subText={subText}
          hideBody={hideBody}
          fitContent={fitContent}
        />
      );

    case 'resource':
      return (
        <NuggetGeneric
          title={title}
          description={body}
          coverImage={variant.previewImage}
          onClick={onClick}
          typeChipProps={[{ label: 'Resource', icon: 'BookOpen01' }]}
          subText={subText}
          hideBody={hideBody}
          fitContent={fitContent}
        />
      );

    case 'text-response':
      return (
        <NuggetGeneric
          title={title}
          description={body}
          onClick={onClick}
          typeChipProps={[{ label: 'Resource', icon: 'BookOpen01' }]}
          subText={subText}
          hideBody={hideBody}
          fitContent={fitContent}
        />
      );

    case 'book':
      return (
        <NuggetGeneric
          title={title}
          description={body}
          coverImage={variant.previewImage}
          onClick={onClick}
          typeChipProps={[{ label: 'Book', icon: 'BookOpen01' }]}
          subText={subText}
          hideBody={hideBody}
          fitContent={fitContent}
        />
      );

    case 'competency':
      return (
        <NuggetGeneric
          title={title}
          description={body}
          onClick={onClick}
          customHeader={
            <NuggetDefaultCoverImage
              variant={{ type: 'COMPETENCY', name: title ?? '', slug: variant.slug }}
            />
          }
          typeChipProps={[{ label: 'Competency', icon: 'Lightbulb05' }]}
          subText={subText}
          hideBody={hideBody}
          fitContent={fitContent}
        />
      );

    case 'scholarships':
      return (
        <NuggetGeneric
          title={title}
          lineItems={[
            {
              title: 'Award Range',
              text:
                variant.scholarshipInfo.award_money_range_min?.toString() +
                ' - ' +
                variant.scholarshipInfo.award_money_range_max?.toString(),
            },
            { title: 'Award Type', text: variant.scholarshipInfo.award_type },
            { title: 'Award Category', text: variant.scholarshipInfo.award_category ?? '' },
            { title: 'Award Deadline', text: variant.scholarshipInfo.submission_deadline ?? '' },
          ]}
          onClick={onClick}
          typeChipProps={[{ label: 'Scholarship', icon: 'CurrencyDollarCircle' }]}
          sponsorChipProps={[
            ...(variant.scholarshipInfo.prog_restrict_corp_affiliation
              ? [
                  {
                    label: variant.scholarshipInfo.prog_restrict_corp_affiliation,
                    defaultIcon: 'Building01' as AppIcons,
                  },
                ]
              : []),
          ]}
          subText={subText}
          hideBody={hideBody}
          fitContent={fitContent}
        />
      );

    case 'internship':
      return (
        <NuggetGeneric
          title={title}
          lineItems={[
            {
              title: 'Type',
              text: variant.internshipInfo.role_type ?? '',
            },
            ...(variant.internshipInfo?.careerPaths
              ? [
                  {
                    title: 'Path',
                    text: variant.internshipInfo.careerPaths.map((path) => path.name).join(', '),
                  },
                ]
              : []),
            { title: 'Industry', text: variant.internshipInfo.industry_name ?? '' },
            { title: 'Location', text: variant.internshipInfo.location ?? '' },
          ]}
          onClick={onClick}
          typeChipProps={[
            {
              label: variant.internshipInfo.role_type ?? 'Job Opportunity',
              icon: 'CurrencyDollarCircle',
            },
          ]}
          sponsorChipProps={[
            {
              label: variant.internshipInfo.company ?? '',
              iconImage: variant.internshipInfo.logo_link,
              defaultIcon: 'Building01',
            },
          ]} // icon to be updated to company logo
          subText={subText}
          hideBody={hideBody}
          fitContent={fitContent}
        />
      );

    case 'profile-external':
      return (
        <NuggetProfile
          variant={{
            type: 'SMALL',
            abbreviatedName: variant.abbreviatedName,
            location: variant.location,
            jobTitle: variant.jobTitle,
            employer: variant.employer,
            school: variant.school,
            profileImageUrl: variant.profileImageUrl,
            profileType: 'EXTERNAL',
            linkToProfile: variant.linkToProfile,
            contentSize,
            onClick,
            subText,
            fitContent,
          }}
        />
      );

    case 'profile-internal':
      return (
        <NuggetProfile
          variant={{
            type: 'SMALL',
            abbreviatedName: variant.abbreviatedName,
            location: variant.location,
            jobTitle: variant.jobTitle,
            employer: variant.employer,
            school: variant.school,
            role: variant.role,
            profileImageUrl: variant.profileImageUrl,
            profileCoverImageUrl: variant.profileCoverImageUrl,
            profileType: 'INTERNAL',
            contentSize,
            linkToProfile: variant.linkToProfile,
            sharedTraitsText: variant.sharedTraitsText,
            onClick,
            subText,
            fitContent,
          }}
        />
      );

    case 'career-path':
      return (
        <NuggetGeneric
          title={title}
          description={body}
          onClick={onClick}
          customHeader={
            <NuggetDefaultCoverImage
              variant={{ type: 'CAREER_PATH', name: variant.careerPathInfo?.name ?? '' }}
            />
          }
          typeChipProps={[
            { label: 'Career Path', icon: 'Rocket02' },
            { label: 'Career Major', icon: 'Atom01' },
          ]}
          subText={subText}
          hideBody={hideBody}
          fitContent={fitContent}
        />
      );

    case 'generated-content':
      return (
        <NuggetGeneric
          title={title}
          description={variant.generatedContentInfo?.summary}
          onClick={onClick}
          typeChipProps={[
            { label: 'For You', icon: 'File02' },
            ...(variant.generatedContentInfo?.minor_source
              ? [
                  {
                    label: variant.generatedContentInfo?.minor_source ?? '',
                    icon: 'File02' as AppIcons,
                  },
                ]
              : []),
          ]}
          sponsorChipProps={[
            ...(variant.generatedContentInfo?.major_source
              ? [
                  {
                    label: variant.generatedContentInfo?.major_source ?? '',
                    iconImage: variant.generatedContentInfo?.profile_image_url,
                    defaultIcon: 'File02' as AppIcons,
                  },
                ]
              : []),
            {
              label: formatDateToLocaleString(variant.generatedContentInfo?.created_at ?? ''),
              defaultIcon: 'Calendar',
            },
          ]}
          subText={subText}
          hideBody={hideBody}
          fitContent={fitContent}
        />
      );

    default:
      return <></>;
  }
};

type PanelNuggetProps = Readonly<{
  title: string;
  body?: string;
  variant: NuggetUXVariant;
  onClick?: () => void;
  subText?: string;
  size?: 'SMALL' | 'LARGE';
  hideBody?: boolean;
  fitContent?: boolean;
}>;

export const PanelNugget = ({
  title,
  body,
  variant,
  onClick,
  subText,
  size = 'LARGE',
  hideBody = false,
  fitContent = false,
}: PanelNuggetProps) => {
  const component = variantMapper(
    variant,
    title,
    size,
    body,
    hideBody,
    fitContent,
    onClick,
    subText,
  );

  return <>{component}</>;
};
